// PATROCÍNIO: a segunda fonte de dinheiro do clube, e uma aposta sua.
//
// A receita da temporada já existia, mas era automática: entrava sozinha e não
// havia nada a decidir. Patrocínio é diferente — você escolhe entre contratos
// com perfis opostos, e a escolha custa ou rende conforme a temporada.
//
//   seguro     paga muito por fora e quase nada de bônus
//   agressivo  paga pouco fixo e muito se você entregar
//
// Quem vai brigar por título assina o agressivo. Quem está reconstruindo com
// elenco fraco assina o seguro, porque bônus que não vem não paga folha.
//
// ⚠️ Os valores saem do `econ` da liga, nunca fixos: na NBB o dinheiro todo é
// ~7,5% do da NBA (moneyScale), e um patrocínio em número absoluto quebraria
// uma das duas economias.

import { makeRng } from './rng.js';

/** As metas que um patrocinador pode cobrar, da mais fácil à mais difícil. */
export const METAS = {
  vitorias: { id: 'vitorias', label: 'fechar a temporada com mais vitórias que derrotas' },
  playoffs: { id: 'playoffs', label: 'chegar aos playoffs' },
  final: { id: 'final', label: 'chegar à final' },
  titulo: { id: 'titulo', label: 'ser campeão' },
};

const MARCAS = [
  'Vulcano', 'Atlas Bebidas', 'NorteBank', 'Zênite Telecom', 'Ferrovia',
  'Solar Energia', 'Cimento Rocha', 'Ipê Seguros', 'Delta Motors', 'Cobre Fino',
  'Aurora Alimentos', 'Praia Grande Turismo', 'Vitória Log', 'Meridiano',
];

/**
 * Três propostas de patrocínio para a temporada que vai começar.
 *
 * O tamanho depende de quanto o clube vale para uma marca: força do elenco e o
 * que ele fez na temporada passada. Um clube que ganhou tudo negocia melhor.
 *
 * @param força      0..1 — o quanto o elenco é bom perto do resto da liga
 * @param aproveitamento 0..1 — vitórias da temporada passada
 */
export function makeSponsorOffers({ econ, seed, season, forca, aproveitamento }) {
  const rng = makeRng(`patrocinio|${seed}|${season}`);

  // A base é uma fração da receita que a liga já paga — assim o patrocínio
  // pesa o mesmo nas duas ligas sem nenhum número escrito na mão.
  const base = econ.revenueBase * 0.32;
  const atratividade = 0.55 + forca * 0.55 + aproveitamento * 0.45;

  const marcas = [...MARCAS].sort(() => rng.range(-1, 1));
  const arredonda = (v) => Math.round(v * 10) / 10;

  const perfis = [
    {
      tipo: 'seguro',
      rotulo: 'Contrato seguro',
      fixoMult: 1.25, bonusMult: 0.35, meta: 'vitorias', anos: 3,
      resumo: 'Paga bem todo ano, sem cobrar título.',
    },
    {
      tipo: 'equilibrado',
      rotulo: 'Contrato equilibrado',
      fixoMult: 0.9, bonusMult: 1.1, meta: 'playoffs', anos: 2,
      resumo: 'Um meio-termo: paga razoável e premia os playoffs.',
    },
    {
      tipo: 'agressivo',
      rotulo: 'Contrato agressivo',
      fixoMult: 0.45, bonusMult: 3.0, meta: forca > 0.62 ? 'titulo' : 'final', anos: 2,
      resumo: 'Fixo baixo. O dinheiro está quase todo no bônus.',
    },
  ];

  return perfis.map((p, i) => ({
    id: `patro-${season}-${p.tipo}`,
    tipo: p.tipo,
    rotulo: p.rotulo,
    resumo: p.resumo,
    marca: marcas[i] || 'Patrocinador',
    fixo: arredonda(base * atratividade * p.fixoMult * rng.range(0.92, 1.08)),
    bonus: arredonda(base * atratividade * p.bonusMult * rng.range(0.92, 1.08)),
    meta: p.meta,
    metaLabel: METAS[p.meta].label,
    anos: p.anos,
    season,
  }));
}

/**
 * A meta do contrato foi cumprida nesta temporada?
 *
 * @param resultado { vitorias, derrotas, chegouPlayoffs, chegouFinal, campeao }
 */
export function metaCumprida(meta, resultado) {
  switch (meta) {
    case 'vitorias': return resultado.vitorias > resultado.derrotas;
    case 'playoffs': return !!resultado.chegouPlayoffs;
    case 'final': return !!resultado.chegouFinal;
    case 'titulo': return !!resultado.campeao;
    default: return false;
  }
}

/**
 * Quanto o patrocinador paga no fim desta temporada.
 *
 * Devolve também o texto do que aconteceu — o jogador precisa entender por que
 * recebeu (ou não) o bônus, senão o contrato vira sorteio.
 */
export function pagamentoDoPatrocinio(sponsor, resultado) {
  if (!sponsor) return { total: 0, bateu: false, texto: null };
  const bateu = metaCumprida(sponsor.meta, resultado);
  const total = Math.round((sponsor.fixo + (bateu ? sponsor.bonus : 0)) * 10) / 10;
  const texto = bateu
    ? `${sponsor.marca}: ${sponsor.fixo.toFixed(1)} mi de contrato + ${sponsor.bonus.toFixed(1)} mi de bônus (${sponsor.metaLabel}).`
    : `${sponsor.marca}: ${sponsor.fixo.toFixed(1)} mi de contrato. O bônus de ${sponsor.bonus.toFixed(1)} mi não veio — a meta era ${sponsor.metaLabel}.`;
  return { total, bateu, texto };
}
